import { useTranslation } from "react-i18next";
import { getCookie } from "typescript-cookie";
import { Modal } from "antd";
import { useDeleteOrganicproducersMutation } from "../../../../redux/api/organic/organicproducers";
import { EntOrganicproducers } from "../../../../models/organic/organicProducers";
import { mode, setUrlSource } from "../../../../utils";

const useOrganicproducers = (modal: any, refresh?: () => void) => {
  const { t } = useTranslation();
  const { confirm } = modal;
  const [deleteOrganicproducers] = useDeleteOrganicproducersMutation();

  const showError = (msg: string) =>
	Modal.error({ title: t("msg.error"), content: msg });

  const showSuccess = (msg: string) =>
	Modal.success({ title: t("msg.success"), content: msg });

  const buildFormData = (data: EntOrganicproducers): FormData => {
	const formData = new FormData();
	const { files, deleteFileIds, ...rest } = data;

	formData.append(
	  "data",
	  new Blob(
		[JSON.stringify({ ...rest, deleteFileIds })],
		{ type: "application/json" }
	  )
	);

	if (files?.length) {
	  files.forEach((file: any) => {
		const fileToAdd = file instanceof File ? file : file.originFileObj;
		if (fileToAdd) {
		  formData.append("files", fileToAdd);
		}
	  });
	}

	return formData;
  };

  const handleResponse = async (response: Response): Promise<any> => {
	if (!response.ok) {
	  const errorText = await response.text();
	  throw new Error(errorText || t("unknownError"));
	}
	const result = await response.json();
	if (result.error) {
	  const lang = getCookie("i18next") === "ky" ? "kg" : "ru";
	  const errorMessage =
		result.error?.data?.res?.[lang] ||
		result.error?.data?.res?.ru ||
		result.error?.data?.msg ||
		t("unknownError");
	  throw new Error(errorMessage);
	}
	return result;
  };

  async function onAddOrganicproducers(data: EntOrganicproducers): Promise<boolean> {
	try {
	  const formData = buildFormData(data);
	  const response = await fetch(`${ setUrlSource(mode) }departments/organic/producers`, {
		method: "POST",
		body: formData,
		headers: {
		  Authorization: `Bearer ${ window.localStorage.getItem("token") }`,
		},
	  });
	  const result = await handleResponse(response);

	  if (result.success) {
		showSuccess(t("msg.successAdd"));
		refresh?.();
		return true;
	  }
	  throw new Error(t("unknownError"));
	} catch (error) {
	  showError(error instanceof Error ? error.message : String(error));
	  return false;
	}
  }

  async function onEditOrganicproducers(data: EntOrganicproducers): Promise<boolean> {
	try {
	  const formData = buildFormData(data);
	  const response = await fetch(`${ setUrlSource(mode) }departments/organic/producers`, {
		method: "PUT",
		body: formData,
		headers: {
		  Authorization: `Bearer ${ window.localStorage.getItem("token") }`,
		},
	  });
	  const result = await handleResponse(response);

	  if (result.success) {
		showSuccess(t("msg.successEdited"));
		refresh?.();
		return true;
	  }
	  throw new Error(t("unknownError"));
	} catch (error) {
	  showError(error instanceof Error ? error.message : String(error));
	  return false;
	}
  }

  async function onDeleteOrganicproducers(organicProducer: EntOrganicproducers): Promise<boolean> {
	return new Promise((resolve) => {
	  confirm({
		centered: true,
		title: t("msg.confirmDeleteText"),
		okText: t("general.yes"),
		cancelText: t("general.no"),
		onOk: async () => {
		  try {
			const response = await deleteOrganicproducers(organicProducer.id as number).unwrap();
			if (response.success) {
			  showSuccess(t("msg.successDelete"));
			  refresh?.();
			  resolve(true);
			} else {
			  throw new Error(t("unknownError"));
			}
		  } catch (error: any) {
			showError(error?.data?.msg || t("unknownError"));
			resolve(false);
		  }
		},
		onCancel: () => resolve(false),
	  });
	});
  }

  return { onAddOrganicproducers, onEditOrganicproducers, onDeleteOrganicproducers };
};

export { useOrganicproducers };
